import { ProcedureStatus, WaypointActionStatusName, WaypointStatusName, SupplyItemStatus, WaypointActionType } from '@caresend/types';
import { hasStatus, initStatus } from '@caresend/utils';
import cloneDeep from 'lodash.clonedeep';
import { defineStore } from 'pinia';
import { watch } from 'vue';
import { flattenUpdatesToPathDefinitions } from '../helpers/helpers.js';
import { usePortalStore } from '../index.js';
import { maybeCancelWaypointAction } from '../set/index.js';
import { getRecordManagerByWorkflowItem, getItemWorkflowByContext } from './getters.js';
import { WorkflowItemStatus } from './model.js';
import { workflowUtils } from './utils.js';
import { autoState } from '../../storeUtilities/state/state.js';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import 'firebase/auth';
import '../../../../../../database/firebaseConfig/cloudFunctions.js';
import '../../../../../../database/firebaseConfig/init.js';
import '../../../../../../database/firebaseConfig/methods.js';
import '../../../../../ui/Icon.vue.js';
import '../../../../../../functions/configuration/icons.js';
import '../../../../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../../../../functions/tracking/index.js';
import '../../../../../../router/helpers/initRouter.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import '../../../../../../store/modules/app/model.js';
import '../../../../../../functions/supplies/scanning.js';
import '../../../../../../store/modules/procedures/model.js';
import { updateSupplyItemStatus } from '../../../../../../functions/supplies/set.js';

const { getNextActionable, getIsChildWorkflowComplete, getIsChildWorkflowFailed, getParentItemsAndWorkflow, isItemEqual, } = workflowUtils;
const useItineraryWorkflowsStore = defineStore('itineraryWorkflows', () => {
    const state = autoState({
        waypointID: null,
    });
    const modalState = autoState({
        routeName: null,
        props: null,
        workflowItem: null,
    });
    const actions = {
        // Write action helpers for components to call here
        setMailInBoxOnWaypointAction: (items, _supplyItems, workflowItem) => {
            var _a, _b;
            const barcode = (_b = (_a = items[0]) === null || _a === void 0 ? void 0 : _a.barcode) === null || _b === void 0 ? void 0 : _b.id;
            if (!barcode)
                return;
            const waypointActionID = workflowItem.recordMeta.referenceID;
            const recordManagerIsMailInActionManager = (manager) => { var _a; return ((_a = manager.getRaw(waypointActionID)) === null || _a === void 0 ? void 0 : _a.type) === WaypointActionType.MAIL_IN; };
            const { waypointActions } = usePortalStore();
            if (!recordManagerIsMailInActionManager(waypointActions)) {
                return;
            }
            waypointActions.setChildOf(waypointActionID, 'mailInBoxBarcode', barcode, 'scan mail-in box barcode');
        },
        setSupplyItemsOnProcedure: (_items, supplyItems, workflowItem) => {
            const { procedures } = usePortalStore();
            procedures.setChildOf(workflowItem.recordMeta.referenceID, 'supplyItems', supplyItems, 'scan procedure supply items');
        },
        setProcedureKitSupplyItem: async (_items, supplyItem, workflowItem) => {
            const procedureID = workflowItem.recordMeta.referenceID;
            const { procedures } = usePortalStore();
            const [kitSupplyItem] = Object.values(supplyItem);
            if (!kitSupplyItem)
                return;
            procedures.setChildOf(procedureID, 'kitSupplyItem', kitSupplyItem, 'add procedure kit supply item');
            await updateSupplyItemStatus(kitSupplyItem.id, SupplyItemStatus.USED);
        },
    };
    /**
     * When the workflow item changes, update the route name and props.
     */
    watch(() => modalState.value.workflowItem, (newVal) => {
        var _a;
        if (!newVal)
            return;
        const { modal: route } = newVal.options;
        if (route) {
            modalState.set.routeName(route.name);
            modalState.set.props((_a = route.props) !== null && _a !== void 0 ? _a : null);
        }
    });
    const portalStore = usePortalStore();
    const processAfterEffect = (workflowItem, rm, referenceID, newStatus) => {
        const valIsStatusObj = (k, v) => k === 'status'
            && typeof v === 'object' && 'status' in v;
        let { after } = workflowItem.options;
        if (newStatus === WorkflowItemStatus.FAILED) {
            if (!workflowItem.options.afterFailure)
                return;
            after = workflowItem.options.afterFailure;
        }
        if (!after)
            return;
        const afterFlattened = flattenUpdatesToPathDefinitions(after);
        afterFlattened.forEach(([key, val]) => {
            /** Override the status object with the intended value to ensure timestamp is accurate */
            if (valIsStatusObj(key, val)) {
                const status = initStatus(val.status);
                rm.setChildOf(referenceID, key, status, true);
            }
            else {
                rm.setChildOf(referenceID, key, val, true);
            }
        });
    };
    const completeWorkflowItem = (workflowItem, { 
    /** Truthy value is indicative this was invoked upon dismissing the modal */
    modalDismissal = false, } = {}, 
    /**
     * When determining whether a workflow with a child workflow is complete,
     * a specific child workflow item can be excluded from the check.
     */
    newChildWfItem, inputStatus = WorkflowItemStatus.COMPLETE) => {
        var _a;
        let status = inputStatus;
        if (hasStatus(workflowItem, status)) {
            return modalState.set.workflowItem(null);
        }
        if (modalDismissal && !((_a = workflowItem.options.modal) === null || _a === void 0 ? void 0 : _a.completeOnClose)) {
            return modalState.set.workflowItem(null);
        }
        const manager = getRecordManagerByWorkflowItem(workflowItem, portalStore);
        if (!manager) {
            return modalState.set.workflowItem(null);
        }
        if (workflowItem.recordMeta.childWorkflow) {
            const isComplete = getIsChildWorkflowComplete(workflowItem, portalStore, newChildWfItem, false, true);
            const isFullyFailed = getIsChildWorkflowFailed(workflowItem, portalStore, newChildWfItem, 'full');
            if (isFullyFailed) {
                status = WorkflowItemStatus.FAILED;
                const isFailureWorkflowComplete = getIsChildWorkflowComplete(workflowItem, portalStore, newChildWfItem, true, true);
                if (!isFailureWorkflowComplete) {
                    return modalState.set.workflowItem(null);
                }
            }
            const isPartialFailed = getIsChildWorkflowFailed(workflowItem, portalStore, newChildWfItem, 'partial');
            if (!isComplete && !isFullyFailed && !isPartialFailed)
                return modalState.set.workflowItem(null);
            if (isPartialFailed && !isComplete)
                return modalState.set.workflowItem(null);
        }
        const workflow = getItemWorkflowByContext(workflowItem, manager);
        if (!workflow) {
            return modalState.set.workflowItem(null);
        }
        const newWorkflowItem = {
            ...workflowItem,
            status: initStatus(status),
        };
        const newWorkflow = workflow.map((item) => {
            if (isItemEqual(item, workflowItem))
                return newWorkflowItem;
            return item;
        });
        const { recordMeta: { referenceID, parent } } = workflowItem;
        const { context } = parent;
        /**
         * Typecasting because at this point in the runtime, enough places have
         * validated that this is allowed.
         */
        manager.setChildOf(referenceID, `workflows/${parent.referenceID}/${context}`, cloneDeep(newWorkflow), true);
        processAfterEffect(workflowItem, manager, referenceID, status);
        const nextActionable = getNextActionable(newWorkflow);
        const { parents: parentItems } = getParentItemsAndWorkflow(workflowItem, portalStore);
        if ((!nextActionable
            || nextActionable.options.nonBlocking
            || hasStatus(status, WorkflowItemStatus.FAILED)) && parentItems) {
            parentItems.forEach((parentItem) => {
                completeWorkflowItem(parentItem, { modalDismissal }, newWorkflowItem, status);
            });
        }
        modalState.set.workflowItem(null);
    };
    // Watch for possibly cancelled proecedures and cancel any workflows associated with them
    watch(() => portalStore.itinerary.getters.procedures(), (procedures, oldProcedures) => {
        const newlyCancelledProcedures = Object.values(procedures).filter((p) => hasStatus(p, ProcedureStatus.CANCELED)
            && !hasStatus(oldProcedures === null || oldProcedures === void 0 ? void 0 : oldProcedures[p.id], ProcedureStatus.CANCELED));
        newlyCancelledProcedures.map((p) => {
            const successWorkflows = portalStore.procedures.getters.workflowsByExecutionType(p, 'success');
            Object.values(successWorkflows).forEach((contexts) => {
                Object.values(contexts).forEach((wf) => {
                    wf.forEach((wfi) => {
                        if (!wfi)
                            return;
                        completeWorkflowItem(wfi, undefined, undefined, WorkflowItemStatus.FAILED);
                    });
                });
            });
            if (!p.waypointActions)
                return;
            Object.keys(p.waypointActions).forEach((wpaID) => {
                maybeCancelWaypointAction(wpaID);
            });
        });
    }, { deep: true });
    watch(() => portalStore.itinerary.getters.currentWaypointActions(), (waypointActions, oldWaypointActions) => {
        const newlyCancelledWaypointActions = Object.values(waypointActions).filter((wpa) => {
            var _a;
            return !!((_a = oldWaypointActions === null || oldWaypointActions === void 0 ? void 0 : oldWaypointActions[wpa.id]) === null || _a === void 0 ? void 0 : _a.status)
                && hasStatus(wpa, WaypointActionStatusName.CANCELED)
                && !hasStatus(oldWaypointActions === null || oldWaypointActions === void 0 ? void 0 : oldWaypointActions[wpa.id], WaypointActionStatusName.CANCELED);
        });
        if (!newlyCancelledWaypointActions.length)
            return;
        newlyCancelledWaypointActions.map((wpa) => {
            const workflows = portalStore.waypointActions.getters.workflowItemsFlattened(wpa);
            workflows.forEach((wf) => completeWorkflowItem(wf, undefined, undefined, WorkflowItemStatus.FAILED));
        });
    }, { deep: true });
    watch(() => portalStore.itinerary.getters.currentWaypoints(), (waypoints, oldWaypoints) => {
        const oldWpMap = new Map(oldWaypoints.map((wp) => [wp.id, wp]));
        const newlyCompletedWaypoints = waypoints.filter((wp) => {
            var _a;
            return !!((_a = oldWpMap.get(wp.id)) === null || _a === void 0 ? void 0 : _a.status)
                && hasStatus(wp, WaypointStatusName.COMPLETE)
                && !hasStatus(oldWpMap.get(wp.id), WaypointStatusName.COMPLETE);
        });
        if (!newlyCompletedWaypoints.length)
            return;
        newlyCompletedWaypoints.forEach((wp) => {
            const workflows = portalStore.waypoints.getters.workflows(wp);
            Object.values(workflows).forEach((contexts) => {
                Object.values(contexts).forEach((wf) => {
                    wf.forEach((wfi) => completeWorkflowItem(wfi));
                });
            });
        });
    });
    return {
        actions,
        completeWorkflowItem,
        modalState,
        state,
    };
});

export { useItineraryWorkflowsStore };
